import store from '@/store/store';
import b64 from 'atob';

import types from '@/models';
import http from './apiConfig';

export default {

  /**
   * Attempts to get a token with given credentials and log in with it.
   * Returns false if either operation fails.
   * @param {Object} data an object containing the email & password
   * @returns {boolean} true if login was successful. false otherwise
   */
  async login (data) {
    let token = '';

    await http.post('auth', data)
      .then(async (response) => {
        if (response.status === 200) token = response.data.token;
        await store.dispatch('updateUserToken', token);
      })
      .catch((e) => {
        console.log(e);
      });

    try {
      let user = await this.fetchAdminUser(token);
      return user !== null;
    } catch (e) {
      return false;
    }
  },

  /**
   * Returns the response from the api
   * @param {string} token
   * @returns {Object|null} user object if login was successful. null otherwise
   */
  async fetchAdminUser (token) {
    try {

      let id = this.extractUserIdFromToken(token);

      let response = await http.get(`admin-users/${id}`, this.getDefaultConfig());

      if (response.status !== 200) {
        return null;
      }

      let user = response.data;

      await store.dispatch('updateUserInfo', user);

      return user
    } catch (e) {
      return null
    }

  },


  /**
   * Returns the response from the api
   * @param {Object} options
   * @returns {Object|null} object containing products if successful. null otherwise
   */
  async fetchProducts (options = {}) {
    try {

      let response = await http.post(`products`, options, this.getDefaultConfig());

      if (response.status !== 200) {
        return [];
      }

      response.data.products = response.data.products.map((it)=>{
        return new types.Product(it);
      });

      return response.data;
    } catch (e) {
      return []
    }

  },
  /**
   * Returns the response from the api
   * @returns {Object|null} object containing products if successful. null otherwise
   */
  async fetchAllProductNames (company_id, include_company_name) {
    try {

      const params = [];
      if (company_id) params.push(`company_id=${company_id}`);
      if (include_company_name === true) params.push(`company_name=1`);
      const queryString = params.length ? `?${params.join('&')}` : '';

      let response = await http.get(`products/all/names-only${queryString}`, this.getDefaultConfig());

      if (response.status !== 200) {
        return [];
      }
      // returns product objects, but only `product_id` and `product_name` are filled
      response.data.products = response.data.products.map((it)=>{
        return new types.Product(it);
      });

      return response.data;
    } catch (e) {
      return []
    }

  },

  /**
   * DO NOT USE - IS VERY SLOW AND CAN FAIL ON API SIDE
   * Gets a Product from the api
   * @param {Number | String} productId
   * @param {Language|int} language
   * @returns {Object|null} product object if successful. null otherwise
   */
  async fetchProduct (productId, language) {
    try {
      let languageId = (isNaN(language) ? language.language_id : language);
      let response = await http.get(`products/${productId}?lang=${languageId}`, this.getDefaultConfig());

      if (response.status !== 200) {
        return null;
      }

      return new types.Product(response.data);
    } catch (e) {
      return null;
    }
  },

  /**
   * Gets a Product from the api
   * @param {Number | String} productId
   * @param {Language|int} language
   * @returns {types.Product|null} product object if successful. null otherwise
   */
  async fetchLeanProduct (productId, language) {
    try {
      let languageId = (isNaN(language) ? language.language_id : language);


      let requests = [
        http.get(`products/${productId}/lean/?lang=${languageId}`, this.getDefaultConfig()),
        http.get(`products/${productId}/lean/product_properties/?lang=${languageId}`, this.getDefaultConfig()),
        http.get(`products/${productId}/lean/company_groups/?lang=${languageId}`, this.getDefaultConfig()),
        http.get(`products/${productId}/lean/company_product_data/?lang=${languageId}`, this.getDefaultConfig()),
        http.get(`products/${productId}/lean/cyclopedia_analysis_categories/?lang=${languageId}`, this.getDefaultConfig()),
        http.get(`products/${productId}/lean/cyclopedia_product_data/?lang=${languageId}`, this.getDefaultConfig()),
      ];

      const results = (await Promise.all(requests)).map((response) => {
        if (response.status !== 200) {
          console.log(`Partial product fetch error: ${response}`);
        }
        return new types.Product(response.data);
      });
      const completeProduct= results[0];
      completeProduct.properties = results[1].properties;
      completeProduct.company_groups = results[2].company_groups;
      completeProduct.company_product_data = results[3].company_product_data;
      completeProduct.cyclopedia_analysis_categories = results[4].cyclopedia_analysis_categories;
      completeProduct.cyclopedia_product_properties = results[5].cyclopedia_product_properties;

      return completeProduct;
    } catch (e) {
      return null;
    }
  },

  /**
   * Returns the response from the api
   * @returns {Array} list of companies. [] if none.
   */
  async fetchAllCompanies () {
    try {

      let response = await http.get(`companies/all`, this.getDefaultConfig());

      if (response.status !== 200) {
        return [];
      }

      response.data.companies = response.data.companies.map((it)=>{
        return new types.Company(it);
      });

      return response.data;
    } catch (e) {
      return []
    }

  },

  /**
   * Returns the response from the api
   * @returns {Array} list of companies. [] if none.
   */
  async fetchActiveCompanies () {
    try {

      let response = await http.get(`companies/active`, this.getDefaultConfig());

      if (response.status !== 200) {
        return [];
      }

      response.data.companies = response.data.companies.map((it)=>{
        return new types.Company(it);
      });

      return response.data.companies;
    } catch (e) {
      return []
    }

  },

  /**
   * Returns the response from the api
   * @param {Company} company
   * @returns {Company} created company if successful, null otherwise
   */
  async createNewCompany (company) {
    try {
      let response = await http.post(`/companies/create`, company, this.getDefaultConfig());
      if (response.status !== 201) return null;
      return new types.Company(response.data);
    } catch (e) {
      return null;
    }
  },

  /**
   * Returns the response from the api
   * @returns {Array} list of company groups. [] if none.
   */
  async fetchCompanyGroups (company) {
    try {
      let companyId = (isNaN(company) ? company.company_id : company);
      let response = await http.get(`companies/${companyId}/groups`, this.getDefaultConfig());

      if (response.status !== 200) {
        return [];
      }

      return response.data.map((it)=>{
        return new types.CompanyGroup(it);
      });
    } catch (e) {
      return []
    }

  },

  /**
   * Returns the response from the api
   * @param {Company|int} company
   * @param {Array} ranks
   * @returns {Array} list of company ranks. [] if none.
   */
  async patchCompanyRanksOrder (company, ranks) {
    try {
      let companyId = (isNaN(company) ? company.company_id : company);
      let response = await http.patch(`companies/${companyId}/ranks/order`, ranks, this.getDefaultConfig());

      if (response.status !== 200) {
        return [];
      }


      return response.data.map((it)=>{
        return new types.CompanyRank(it);
      });
    } catch (e) {
      return []
    }

  },

  /**
   * Returns the response from the api
   * @param {CompanyRank} companyRank
   * @param {Array} localizations
   * @returns {CompanyRank} the CompanyRank with it's updated localizations.
   */
  async putCompanyRankLocalizations (companyRank, localizations) {
    try {
      let response = await http.put(`companies/${companyRank.company_id}/ranks/${companyRank.id}/localizations`, localizations, this.getDefaultConfig());

      if (response.status !== 200) {
        return new types.CompanyRank(companyRank);
      }


      return new types.CompanyRank(response.data);
    } catch (e) {
      return new types.CompanyRank(companyRank);
    }

  },

  /**
   * Returns the response from the api
   * @param {CompanyRank} rank
   * @returns {CompanyRank} the newly created rank
   */
  async createCompanyRank (rank) {
    try {
      let response = await http.post(`/companies/${rank.company_id}/ranks`, rank, this.getDefaultConfig());
      if (response.status !== 201) {
        return null;
      }
      return new types.CompanyRank(response.data);
    } catch (e) {
      return null;
    }
  },

  /**
   * Returns the response from the api
   * @param {CompanyRank} rank
   * @returns {CompanyRank} the newly created rank
   */
  async deleteCompanyRank (rank) {
    try {
      let response = await http.delete(`/companies/${rank.company_id}/ranks/${rank.id}`, this.getDefaultConfig());
      return response.status === 200;
    } catch (e) {
      return false;
    }
  },

  /**
   * Returns the response from the api
   * @returns {Array} list of company groups. [] if none.
   */
  async fetchCompany (companyId) {
    try {
      let response = await http.get(`companies/${companyId}`, this.getDefaultConfig());

      if (response.status !== 200) {
        return new types.Company();
      }

      return new types.Company(response.data);
    } catch (e) {
      return []
    }

  },

  /**
   * Gets a Company from the api
   * @param {Number | String} companyId
   * @returns {types.Company|null} company object if successful. null otherwise
   */
  async fetchLeanCompany (companyId) {
    try {
      let requests = [
        http.get(`companies/${companyId}/lean`, this.getDefaultConfig()),
        http.get(`companies/${companyId}/groups`, this.getDefaultConfig()),
        http.get(`companies/${companyId}/ranks`, this.getDefaultConfig()),
      ];

      const results = (await Promise.all(requests)).map((response) => {
        if (response.status !== 200) {
          console.log(`Partial company fetch error: ${response}`);
        }
        return response.data;
      });
      console.log(results);
      const completeCompany= new types.Company(results[0]);
      completeCompany.groups = results[1];
      completeCompany.ranks = results[2];

      return completeCompany;
    } catch (e) {
      return null;
    }
  },

  /**
   * Returns the response from the api
   * @returns {Array} list of products for given company. null if none.
   */
  async fetchProductsForCompany (companyId) {
    try {
      let response = await http.get(`companies/${companyId}/products`, this.getDefaultConfig());

      // console.log(response);
      if (response.status !== 200) {
        return null;
      }

      return response.data.map((it)=>{
        return new types.Product(it);
      });

    } catch (e) {
      return null;
    }

  },

  /**
   * only updates 'active' and/or 'name' properties on Company
   * @param {Company} company
   * @returns {Company} the updated company (or null if failure)
   */
  async patchCompany (company) {
    try {
      let response = await http.patch(`/companies/${company.company_id}`, company, this.getDefaultConfig());
      if (response.status !== 200) {
        return null;
      }
      return new types.Company(response.data);
    } catch (e) {
      return null;
    }
  },

  /**
   * only updates 'active' and/or 'name' properties on Company
   * @param {CompanyGroup} group
   * @returns {boolean} true if success
   */
  async deleteCompanyProductGroup (group) {
    try {
      let response = await http.delete(`/companies/${group.parent_company_id}/groups/${group.id}`, this.getDefaultConfig());
      return response.status === 200;
    } catch (e) {
      return false;
    }
  },

  /**
   * only updates 'active' and/or 'name' properties on Company
   * @param {CompanyGroup} group
   * @returns {CompanyGroup} newly crated company group
   */
  async createCompanyProductGroup (group) {
    try {
      let response = await http.post(`/companies/${group.parent_company_id}/groups`, group, this.getDefaultConfig());
      if (response.status !== 201) {
        console.log(response.data);
        return null;
      }
      return new types.CompanyGroup(response.data);
    } catch (e) {
      return null;
    }
  },

  /**
   * Returns the response from the api
   * @param {CompanyGroup} companyGroup
   * @param {Array} localizations
   * @returns {CompanyGroup} the CompanyGroup with it's updated localizations.
   */
  async putCompanyGroupLocalizations (companyGroup, localizations) {
    try {
      let response = await http.put(`companies/${companyGroup.parent_company_id}/groups/${companyGroup.id}/localizations`, localizations, this.getDefaultConfig());

      if (response.status !== 200) {
        console.log(response.data);
        return new types.CompanyGroup(companyGroup);
      }


      return new types.CompanyGroup(response.data);
    } catch (e) {
      return new types.CompanyGroup(companyGroup);
    }

  },

  /**
   * Returns the response from the api
   * @param {Product|int} product
   * @returns {Array} list of the products related to the given product. [] if none.
   */
  async fetchRelatedProducts (product) {
    try {
      let productId = (isNaN(product) ? product.product_id : product);
      let response = await http.get(`products/${productId}/related-products`, this.getDefaultConfig());

      if (response.status !== 200) {
        console.log(response);
        return [];
      }

      return response.data.map((it)=>{
        return new types.Product(it);
      });

    } catch (e) {
      return [];
    }

  },

  /**
   * Returns the response from the api
   * @param {Product|int} product
   * @param {Product|int} toRelate
   * @returns {Boolean} true if successful, false otherwise
   */
  async addRelatedProduct (product, toRelate) {
    // allow either an object or just an id
    let productId = (isNaN(product) ? product.product_id : product);
    let toRelateId = (isNaN(toRelate) ? toRelate.product_id : toRelate);
    try {
      let response = await http.post(`/products/${productId}/related-products/${toRelateId}`, null, this.getDefaultConfig());
      return response.status === 201;
    } catch (e) {
      return false;
    }
  },

  /**
   * Returns the response from the api
   * @param {Product|int} product
   * @param {Product|int} toUnRelate
   * @returns {Boolean} true if successful, false otherwise
   */
  async removeRelatedProduct (product, toUnRelate) {
    // allow either an object or just an id
    let productId = (isNaN(product) ? product.product_id : product);
    let toUnRelateId = (isNaN(toUnRelate) ? toUnRelate.product_id : toUnRelate);
    try {
      let response = await http.delete(`/products/${productId}/related-products/${toUnRelateId}`, this.getDefaultConfig());
      return response.status === 200;
    } catch (e) {
      return false;
    }
  },

  /**
   * Returns the response from the api
   * @param {Product|int} product
   * @param {CompanyGroup|int} group
   * @returns {Boolean} true if successful, false otherwise
   */
  async deleteProductFromCompanyGroup (product, group) {
    // allow either an object or just an id
    let productId = (isNaN(product) ? product.product_id : product);
    let groupId = (isNaN(group) ? group.id : group);
    try {
      let response = await http.delete(`/products/${productId}/company-groups/${groupId}`, this.getDefaultConfig());
      return response.status === 200;
    } catch (e) {
      return false;
    }
  },

  /**
   * Returns the response from the api
   * @param {Product|int} product
   * @param {CompanyGroup|int} group
   * @returns {Boolean} true if successful, false otherwise
   */
  async addProductToCompanyGroup (product, group) {
    // allow either an object or just an id
    let productId = (isNaN(product) ? product.product_id : product);
    let groupId = (isNaN(group) ? group.id : group);
    try {
      let response = await http.post(`/products/${productId}/company-groups/${groupId}`, null, this.getDefaultConfig());
      return response.status === 201;
    } catch (e) {
      return false;
    }
  },

  /**
   * Returns the response from the api
   * @param {Product|int} product
   * @param {CyclopediaAnalysisCategory|int} analysisCategory
   * @returns {Boolean} true if successful, false otherwise
   */
  async deleteProductFromAnalysisCategory (product, analysisCategory) {
    // allow either an object or just an id
    let productId = (isNaN(product) ? product.product_id : product);
    let analysisCategoryId = (isNaN(analysisCategory) ? analysisCategory.id : analysisCategory);
    try {
      let response = await http.delete(`/products/${productId}/cyclopedia-analysis-categories/${analysisCategoryId}`, this.getDefaultConfig());
      return response.status === 200;
    } catch (e) {
      return false;
    }
  },

  /**
   * Returns the response from the api
   * @param {Product|int} product
   * @param {CyclopediaAnalysisCategory|int} analysisCategory
   * @returns {Boolean} true if successful, false otherwise
   */
  async addProductToAnalysisCategory (product, analysisCategory) {
    // allow either an object or just an id
    let productId = (isNaN(product) ? product.product_id : product);
    let analysisCategoryId = (isNaN(analysisCategory) ? analysisCategory.id : analysisCategory);
    try {
      let response = await http.post(`/products/${productId}/cyclopedia-analysis-categories/${analysisCategoryId}`, null, this.getDefaultConfig());
      return response.status === 201;
    } catch (e) {
      return false;
    }
  },

  /**
   * Returns the response from the api
   * @param {Localization} localization
   * @returns {Boolean} true if successful, false otherwise
   */
  async saveProductLocalization (localization) {
    try {
      let response = await http.post(`/products/${localization.product_id}/localizations`, localization, this.getDefaultConfig());
      // the endpoint returns either a 201 or a 200 depending on whether a new record was created or not
      return response.status === 200 || response.status === 201;
    } catch (e) {
      return false;
    }
  },


  /**
   * Returns the response from the api
   * @param {Product} product
   * @returns {Boolean} true if successful, false otherwise
   */
  async saveProductBaseProperties (product) {
    try {
      let response = await http.put(`/products/${product.product_id}`, product, this.getDefaultConfig());
      // the endpoint returns either a 201 or a 200 depending on whether a new record was created or not
      return response.status === 200;
    } catch (e) {
      return false;
    }
  },
  /**
   * Returns the response from the api
   * @param {Product} product
   * @returns {Product} true if successful, false otherwise
   */
  async createNewProduct (product) {
    try {
      let response = await http.post(`/products/create`, product, this.getDefaultConfig());
      if (response.status !== 201) return null;
      return new types.Product(response.data);
    } catch (e) {
      return null;
    }
  },
  /**
   * Returns the response from the api
   * @param {Product} product
   * @returns {boolean} true if successful, false otherwise
   */
  async deleteProduct (product) {
    try {
      let response = await http.delete(`/products/${product.product_id}`, this.getDefaultConfig());
      return response.status === 200;
    } catch (e) {
      return false;
    }
  },

  /**
   * Returns the response from the api
   * @param {Product|int} product
   * @returns {Array} list of companies. [] if none.
   */
  async fetchProductDnaTraitScores (product) {
    let productId = (isNaN(product) ? product.product_id : product);
    try {

      let response = await http.get(`/products/${productId}/dna/trait-scores`, this.getDefaultConfig());

      if (response.status !== 200) {
        return [];
      }

      return response.data.map((it)=>{
        return new types.ProductDnaTraitScore(it);
      });
    } catch (e) {
      return []
    }

  },
  /**
   * Returns the response from the api
   * @param {ProductDnaTraitScore} productDnaTraitScore
   * @returns {boolean} true if successful, false otherwise
   */
  async createProductDnaTraitScore (productDnaTraitScore) {
    try {
      let response = await http.post(`/products/${productDnaTraitScore.product_id}/dna/trait-scores`, productDnaTraitScore, this.getDefaultConfig());
      return response.status === 201;
    } catch (e) {
      return false;
    }
  },
  /**
   * Returns the response from the api
   * @param {types.ProductDnaTraitScore} productDnaTraitScore
   * @returns {Boolean} true if successful, false otherwise
   */
  async deleteProductDnaTraitScore (productDnaTraitScore) {
    try {
      let response = await http.delete(`/products/${productDnaTraitScore.product_id}/dna/trait-scores/${productDnaTraitScore.id}`, this.getDefaultConfig());
      return response.status === 200;
    } catch (e) {
      return false;
    }
  },

  /**
   * Returns the response from the api
   * @param {ProductProperty} productProperty
   * @returns {ProductProperty} true if successful, false otherwise
   */
  async createProductProperty (productProperty) {
    try {
      let response = await http.post(`/products/${productProperty.product_id}/properties`, productProperty, this.getDefaultConfig());
      if (response.status !== 201) return null;
      return new types.ProductProperty(response.data);
    } catch (e) {
      return null;
    }
  },
  /**
   * Returns the response from the api
   * @param {ProductProperty} productProperty
   * @returns {Boolean} true if successful, false otherwise
   */
  async updateProductProperty (productProperty) {
    try {
      let response = await http.put(`/products/${productProperty.product_id}/properties/${productProperty.id}`, productProperty, this.getDefaultConfig());
      return response.status === 200;
    } catch (e) {
      return false;
    }
  },
  /**
   * Returns the response from the api
   * @param {types.ProductProperty} productProperty
   * @returns {Boolean} true if successful, false otherwise
   */
  async deleteProductProperty (productProperty) {
    try {
      let response = await http.delete(`/products/${productProperty.product_id}/properties/${productProperty.id}`, this.getDefaultConfig());
      return response.status === 200;
    } catch (e) {
      return false;
    }
  },

  /**
   * Returns the response from the api
   * @param {number} productId
   * @param {types.ProductProperty} cyclopediaProductProperty
   * @returns {types.CyclopediaProductProperty} true if successful, false otherwise
   */
  async createCyclopediaProductProperty (productId, cyclopediaProductProperty) {
    try {
      let response = await http.post(`/products/${productId}/cyclopedia-properties`, cyclopediaProductProperty, this.getDefaultConfig());
      if (response.status !== 201) return null;
      return new types.CyclopediaProductProperty(response.data);
    } catch (e) {
      return null;
    }
  },
  /**
   * Returns the response from the api
   * @param {number} productId
   * @param {CyclopediaProductProperty} cyclopediaProductProperty
   * @returns {Boolean} true if successful, false otherwise
   */
  async updateCyclopediaProductProperty (productId, cyclopediaProductProperty) {
    try {
      let response = await http.put(`/products/${productId}/cyclopedia-properties/${cyclopediaProductProperty.id}`, cyclopediaProductProperty, this.getDefaultConfig());
      return response.status === 200;
    } catch (e) {
      return false;
    }
  },
  /**
   * Returns the response from the api
   * @param {number} productId
   * @param {types.CyclopediaProductProperty} cyclopediaProductProperty
   * @returns {Boolean} true if successful, false otherwise
   */
  async deleteCyclopediaProductProperty (productId, cyclopediaProductProperty) {
    try {
      let response = await http.delete(`/products/${productId}/cyclopedia-properties/${cyclopediaProductProperty.id}`, this.getDefaultConfig());
      return response.status === 200;
    } catch (e) {
      return false;
    }
  },

  /**
   * Returns the response from the api
   * @returns {Array} list of cyclopedias. [] if none.
   */
  async fetchAllCyclopedias () {
    try {

      let response = await http.get(`cyclopedias/all`, this.getDefaultConfig());

      if (response.status !== 200) {
        return [];
      }

      return response.data.map((it)=>{
        return new types.Cyclopedia(it);
      });
    } catch (e) {
      return []
    }

  },

  /**
   * Returns the response from the api
   * @param {Cyclopedia|int} cyclopedia
   * @returns {Cyclopedia} the cyclopedia with the given id. null if none.
   */
  async fetchCyclopedia (cyclopedia) {
    try {
      // allow either an object or just an id
      let cyclopediaId = (isNaN(cyclopedia) ? cyclopedia.id : cyclopedia);
      let response = await http.get(`cyclopedias/${cyclopediaId}`, this.getDefaultConfig());

      if (response.status !== 200) {
        return null;
      }
      return new types.Cyclopedia(response.data);
    } catch (e) {
      return null;
    }

  },

  /**
   * Returns the response from the api
   * @param {Cyclopedia} cyclopedia
   * @param {Array} localizations
   * @returns {Cyclopedia} the Cyclopedia with it's updated localizations.
   */
  async putCyclopediaLocalizations (cyclopedia, localizations) {
    try {
      let response = await http.put(`cyclopedias/${cyclopedia.id}/localizations`, localizations, this.getDefaultConfig());

      if (response.status !== 200) {
        return new types.Cyclopedia(cyclopedia);
      }


      return new types.Cyclopedia(response.data);
    } catch (e) {
      return new types.Cyclopedia(cyclopedia);
    }

  },

  /**
   * Returns the response from the api
   * @param {Cyclopedia} cyclopedia
   * @returns {Cyclopedia} created company if successful, null otherwise
   */
  async createNewCyclopedia (cyclopedia) {
    try {
      let response = await http.post(`/cyclopedias/create`, cyclopedia, this.getDefaultConfig());
      if (response.status !== 201) return null;
      return new types.Cyclopedia(response.data);
    } catch (e) {
      return null;
    }
  },

  async updateCyclopedia(cyclopedia) {
    const cyclopediaId = cyclopedia.id;
    delete cyclopedia.id;

    return await http.patch(`/cyclopedias/${cyclopediaId}`, cyclopedia,  this.getDefaultConfig())
      .then(response => response.status === 200)
      .catch(e => {
        console.log('updateCyclopediaError: ', e);
        return false;
      })
  },

  /**
   * deletes a cyclopedia
   * @param {Cyclopedia} cyclopedia
   * @returns {boolean} true if delete was successful
   */
  async deleteCyclopedia (cyclopedia) {
    try {
      let response = await http.delete(`/cyclopedias/${cyclopedia.id}`, this.getDefaultConfig());
      return response.status === 200;
    } catch (e) {
      return false;
    }
  },

  /**
   * Returns the response from the api
   * @param {Cyclopedia|int} cyclopedia
   * @param {Company|int} company
   * @returns {Boolean} true if successful, false otherwise
   */
  async deleteCompanyFromCyclopedia (cyclopedia, company) {
    // allow either an object or just an id
    let cyclopediaId = (isNaN(cyclopedia) ? cyclopedia.id : cyclopedia);
    let companyId = (isNaN(company) ? company.company_id : company);
    try {
      let response = await http.delete(`/cyclopedias/${cyclopediaId}/companies/${companyId}`, this.getDefaultConfig());
      return response.status === 200;
    } catch (e) {
      return false;
    }
  },

  /**
   * Returns the response from the api
   * @param {Cyclopedia|int} cyclopedia
   * @param {Company|int} company
   * @returns {Boolean} true if successful, false otherwise
   */
  async addCompanyToCyclopedia (cyclopedia, company) {
    console.log(company);
    // allow either an object or just an id
    let cyclopediaId = (isNaN(cyclopedia) ? cyclopedia.id : cyclopedia);
    let companyId = (isNaN(company) ? company.company_id : company);
    try {
      let response = await http.post(`/cyclopedias/${cyclopediaId}/companies/${companyId}`, null, this.getDefaultConfig());
      return response.status === 201;
    } catch (e) {
      return false;
    }
  },

  /**
   * Returns the response from the api
   * @param {CyclopediaAnalysis|int} analysis
   * @returns {CyclopediaAnalysis} the analysis with the given id. null if none.
   */
  async fetchAnalysis (analysis) {
    try {
      // allow either an object or just an id
      let analysisId = (isNaN(analysis) ? analysis.id : analysis);
      // we can just anything as the id for the cyclopedia because the api doesn't verify
      let response = await http.get(`/cyclopedias/0/analyses/${analysisId}`, this.getDefaultConfig());

      if (response.status !== 200) {
        return null;
      }
      return new types.CyclopediaAnalysis(response.data);
    } catch (e) {
      return null;
    }

  },

  /**
   * Returns the response from the api
   * @param {CyclopediaAnalysisCategory} category
   * @returns {CyclopediaAnalysisCategory} created CyclopediaAnalysisCategory if successful, null otherwise
   */
  async createNewAnalysisCategory (category) {
    try {
      if (!category.free_description) {
        category.free_description = '';
      }
      let response = await http.post(`/cyclopedias/0/analyses/${category.cyclopedia_analysis_id}/categories/create`, category, this.getDefaultConfig());
      if (response.status !== 201) return null;
      return new types.CyclopediaAnalysisCategory(response.data);
    } catch (e) {
      return null;
    }
  },

  /**
   * Returns the response from the api
   * @param {CyclopediaAnalysis} analysis
   * @returns {CyclopediaAnalysis} created CyclopediaAnalysis if successful, null otherwise
   */
  async createNewAnalysis (analysis) {
    try {
      let response = await http.post(`/cyclopedias/${analysis.cyclopedia_id}/analyses/create`, analysis, this.getDefaultConfig());
      if (response.status !== 201) return null;
      return new types.CyclopediaAnalysis(response.data);
    } catch (e) {
      return null;
    }
  },

  /**
   * deletes a cyclopedia
   * @param {CyclopediaAnalysis} analysis
   * @returns {boolean} true if delete was successful
   */
  async deleteAnalysis (analysis) {
    try {
      let response = await http.delete(`/cyclopedias/${analysis.cyclopedia_id}/analyses/${analysis.id}`, this.getDefaultConfig());
      return response.status === 200;
    } catch (e) {
      return false;
    }
  },

  /**
   * Returns the response from the api
   * @param {CyclopediaAnalysis} analysis
   * @param {Array} localizations
   * @returns {CyclopediaAnalysis} the CyclopediaAnalysis with its updated localizations.
   */
  async putAnalysisLocalizations (analysis, localizations) {
    try {
      let response = await http.put(`cyclopedias/${analysis.cyclopedia_id}/analyses/${analysis.id}/localizations`, localizations, this.getDefaultConfig());

      if (response.status !== 200) {
        return new types.CyclopediaAnalysis(analysis);
      }


      return new types.CyclopediaAnalysis(response.data);
    } catch (e) {
      return new types.CyclopediaAnalysis(analysis);
    }

  },

  /**
   * DO NOT USE - CAN BE VERY SLOW AND TIME OUT
   * Returns the response from the api
   * @param {CyclopediaAnalysisCategory|int} category
   * @returns {CyclopediaAnalysisCategory} the analysis with the given id. null if none.
   */
  async fetchAnalysisCategory (category) {
    try {
      // allow either an object or just an id
      let categoryId = (isNaN(category) ? category.id : category);
      // we can just send anything as the id for the cyclopedia and analyses because the api doesn't verify
      let response = await http.get(`/cyclopedias/0/analyses/0/categories/${categoryId}`, this.getDefaultConfig());

      if (response.status !== 200) {
        return null;
      }
      return new types.CyclopediaAnalysisCategory(response.data);
    } catch (e) {
      return null;
    }

  },

  /**
   * Returns the response from the api
   * @param {CyclopediaAnalysisCategory|int} category
   * @returns {CyclopediaAnalysisCategory} the analysis with the given id. null if none.
   */
  async fetchLeanAnalysisCategory (category) {
    try {
      // allow either an object or just an id
      let categoryId = (isNaN(category) ? category.id : category);
      // we can just send anything as the id for the cyclopedia and analyses because the api doesn't verify
      let requests = [
        http.get(`/cyclopedias/0/analyses/0/categories/${categoryId}/lean/`, this.getDefaultConfig()),
        http.get(`/cyclopedias/0/analyses/0/categories/${categoryId}/lean/products/`, this.getDefaultConfig()),
        http.get(`/cyclopedias/0/analyses/0/categories/${categoryId}/lean/analysis/`, this.getDefaultConfig()),
      ];

      const results = (await Promise.all(requests)).map((response) => {
        if (response.status !== 200) {
          console.log(`Partial CyclopediaAnalysisCategory fetch error: ${response}`);
        }
        return new types.CyclopediaAnalysisCategory(response.data);
      });
      const completeCategory= results[0];
      completeCategory.products = results[1].products;
      completeCategory.analysis = results[2].analysis;

      return completeCategory;
    } catch (e) {
      return null;
    }

  },

  /**
   * deletes a CyclopediaAnalysisCategory
   * @param {CyclopediaAnalysisCategory} category
   * @returns {boolean} true if delete was successful
   */
  async deleteAnalysisCategory (category) {
    try {
      let response = await http.delete(`/cyclopedias/0/analyses/0/categories/${category.id}`, this.getDefaultConfig());
      return response.status === 200;
    } catch (e) {
      return false;
    }
  },

  /**
   * Returns the response from the api
   * @param {CyclopediaAnalysisCategory} category
   * @param {Array} localizations
   * @returns {CyclopediaAnalysisCategory} the CyclopediaAnalysisCategory with its updated localizations.
   */
  async putAnalysisCategoryLocalizations (category, localizations) {
    try {
      let response = await http.put(`cyclopedias/0/analyses/${category.cyclopedia_analysis_id}/categories/${category.id}/localizations`, localizations, this.getDefaultConfig());

      if (response.status !== 200) {
        return new types.CyclopediaAnalysisCategory(category);
      }


      return new types.CyclopediaAnalysisCategory(response.data);
    } catch (e) {
      return new types.CyclopediaAnalysisCategory(category);
    }

  },

  /**
   * Returns the updated metric data
   * @param {FaceScanProductData} faceScanMetricData
   * @param {Product|int} product
   * @returns {Boolean} FaceScanProductData if successful, null otherwise
   */
  async updateFaceScanProductMetric(faceScanMetricData, product) {
    try {
      let productId = (isNaN(product) ? product.product_id : product);
      let response = await http.post(`/products/${productId}/face-scan-values`, faceScanMetricData, this.getDefaultConfig());
      if (response.status === 200 || response.status === 201) {
        return true;
      }
    } catch (e) {
      console.log(e);
      return false;
    }
  },
  /**
   * Returns array of FaceScanProductData
   * @param {Product|int} product
   * @returns {Promise<Array<FaceScanProductData>>} list of product types. [] if none.
   */
  async fetchProductFaceScanData(product) {
    try {
      // allow either an object or just an id
      let productId = (isNaN(product) ? product.product_id : product);

      let response = await http.get(`products/${productId}/face-scan-values`, this.getDefaultConfig());

      if (response.status !== 200) {
        console.log(response.data);
        return [];
      }

      return response.data.map((it)=>{
        return new types.FaceScanProductData(it);
      });
    } catch (e) {
      console.log(e);
      return []
    }

  },

  /**
   * Returns the response from the api
   * @returns {Array} list of companies. [] if none.
   */
  async fetchAllLanguages () {
    try {

      let response = await http.get(`languages/all`, this.getDefaultConfig());

      if (response.status !== 200) {
        return [];
      }

      return response.data.map((it)=>{
        return new types.Language(it);
      });
    } catch (e) {
      return []
    }

  },

  /**
   * Returns the response from the api
   * @returns {Array} list of product types. [] if none.
   */
  async fetchProductTypes () {
    try {

      let response = await http.get(`products/types`, this.getDefaultConfig());

      if (response.status !== 200) {
        return [];
      }

      return response.data.map((it)=>{
        return new types.ProductType(it);
      });
    } catch (e) {
      return []
    }

  },

  /**
   * Returns the response from the api
   * @param {Product|int} product
   * @returns {Array} list of product types. [] if none.
   */
  async fetchProductFrequencies (product) {
    try {
      // allow either an object or just an id
      let productId = (isNaN(product) ? product.product_id : product);

      let response = await http.get(`products/${productId}/frequencies`, this.getDefaultConfig());

      if (response.status !== 200) {
        return [];
      }

      return response.data.map((it)=>{
        return new types.ProductFrequency(it);
      });
    } catch (e) {
      return []
    }

  },

  /**
   * Returns the response from the api
   * @param {ProductFrequency} frequency
   * @returns {ProductFrequency} newly created resource, or null if failure. [] if none.
   */
  async createProductFrequency (frequency) {
    try {
      let response = await http.post(`products/${frequency.product_id}/frequencies`, frequency, this.getDefaultConfig());

      if (response.status !== 201) {
        return null;
      }

      return new types.ProductFrequency(response.data);
    } catch (e) {
      return null;
    }

  },

  /**
   * Saves image to Product as it's primary product image
   * @param {Product} product
   * @param image - image file
   * @returns {Array} newly created resources, or null if failure.
   */
  async postProductPrimaryImage (product, image) {
    try {
      let response = await this.postImageFile(image, `/products/${product.product_id}/images/primary`);
      console.log(response.data);
      if (response.status !== 201) {
        return null;
      }

      return response.data.map((it)=>{
        return new types.Image(it);
      });
    } catch (e) {
      return null;
    }

  },

  /**
   * Saves image to Product as it's secondary/background image
   * @param {Product} product
   * @param image - image file
   * @returns {Array} newly created resources, or null if failure.
   */
  async postProductSecondaryImage (product, image) {
    try {
      let response = await this.postImageFile(image, `/products/${product.product_id}/images/secondary`);
      console.log(response.data);
      if (response.status !== 201) {
        return null;
      }

      return response.data.map((it)=>{
        return new types.Image(it);
      });
    } catch (e) {
      return null;
    }

  },

  /**
   * Saves image to Cyclopedia as it's product image
   * @param {Cyclopedia} cyclopedia
   * @param image - image file
   * @returns {Array} newly created resources, or null if failure.
   */
  async postCyclopediaProductImage (cyclopedia, image) {
    try {
      let response = await this.postImageFile(image, `/cyclopedias/${cyclopedia.id}/images/product`);
      if (response.status !== 201) return null;

      return response.data.map((it)=>{
        return new types.Image(it);
      });
    } catch (e) {
      return null;
    }
  },

  /**
   * Saves image to Cyclopedia as it's hero image
   * @param {Cyclopedia} cyclopedia
   * @param image - image file
   * @returns {Array} newly created resources, or null if failure.
   */
  async postCyclopediaHeroImage (cyclopedia, image) {
    try {
      let response = await this.postImageFile(image, `/cyclopedias/${cyclopedia.id}/images/hero`);
      if (response.status !== 201) return null;

      return response.data.map((it)=>{
        return new types.Image(it);
      });
    } catch (e) {
      return null;
    }
  },

  /**
   * Saves image to Cyclopedia as it's hero image
   * @param {CyclopediaAnalysisCategory} category
   * @param image - image file
   * @returns {Array} newly created resources, or null if failure.
   */
  async postCyclopediaAnalysisCategoryHeroImage (category, image) {
    try {
      let response = await this.postImageFile(image, `/cyclopedias/0/analyses/0/categories/${category.id}/images/hero`);
      if (response.status !== 201) return null;

      return response.data.map((it)=>{
        return new types.Image(it);
      });
    } catch (e) {
      return null;
    }
  },

  /**
   * Sends a file as part of a form-data object
   * @param file - image file
   * @param url - the url to post the request to
   * @param imageFieldName - the name of the form-field the image will be attached to
   * @returns {ProductFrequency} newly created resource, or null if failure. [] if none.
   */
  async postImageFile(file, url, imageFieldName = 'image') {

    console.log('foobar');
    let formData = new FormData();
    formData.append(imageFieldName, file);

    let config = this.getDefaultConfig();
    config.headers['Content-Type'] = 'multipart/form-data';

    return await http.post(url, formData, config);
  },

  /**
   * extracts the user_id (id) from a JWT token.
   * It will use the atob library by default (required for
   * tests to work) and fall back on window.atob if it fails (
   * which seems to be the case in a browser)
   * @param {string} token - JWT token
   * @param {boolean?} useWindow - use the dom's window.atob function
   * @returns {Object} the payload object or {} if unsuccessful
   */
  extractPayloadFromToken(token, useWindow) {
    try {
      let payloadString = token.split('.')[1];

      let base64 = payloadString.replace(/-/g, '+').replace(/_/g, '/');

      let map;
      if (useWindow){
        map = JSON.parse(window.atob(base64));
      } else {
        map = JSON.parse(b64.atob(base64));
      }

      return map;
    } catch (e) {
      if (!useWindow) {
        // the b64 library fails sometimes. fallback on window.
        return this.extractPayloadFromToken(token, true);
      }
      return {};
    }

  },


  /**
   * extracts the user_id (id) from a JWT token.
   * It will use the atob library by default (required for
   * tests to work) and fall back on window.atob if it fails (
   * which seems to be the case in a browser)
   * @param {string} token - JWT token
   * @returns {number} the user_id embedded in the token or 0 if unsuccessful
   */
  extractUserIdFromToken(token) {
    let tokenData = this.extractPayloadFromToken(token);
    if (Object.prototype.hasOwnProperty.call(tokenData, 'id')) {
      return tokenData['id'];
    }
    return 0;
  },

  /**
   * extracts the expiration from a JWT token and compares it to now().
   * @param {string} token - JWT token
   * @returns {boolean} true if the token is expired or otherwise invalid
   */
  isTokenExpired(token) {
    let tokenData = this.extractPayloadFromToken(token);
    if (!Object.prototype.hasOwnProperty.call(tokenData, 'exp')) {
      return true;
    }
    return Date.now() >= (tokenData['exp'] * 1000);
  },

  /**
   * Returns the default axios config for the api
   * @returns {AxiosRequestConfig|Object}
   */
  getDefaultConfig() {
    return { headers: this.getRequestAuthHeaders() };
  },

  /**
   * Returns the auth header with bearer token for the itovi api
   * @returns {Object}
   */
  getRequestAuthHeaders() {
    const token = store.state.activeUserToken;
    return { 'Authorization': `Bearer ${token}` }
  },

  /**
   * Returns the response from the api
   * @param {Object} options
   * @returns {Object|null} object containing products if login was successful. null otherwise
   */
  async fetchUsers (options = {}) {
    try {

      let response = await http.post(`users`, options, this.getDefaultConfig());

      if (response.status !== 200) {
        return [];
      }

      response.data.users = response.data.users.map((it)=>{
        return new types.User(it);
      });

      return response.data;
    } catch (e) {
      return []
    }

  },

  /**
   * Returns the response from the api
   * @param {int} id
   * @returns {Object|null} user object. null otherwise
   */
  async fetchUser (id) {
    try {

      let response = await http.get(`users/${id}`, this.getDefaultConfig());

      if (response.status !== 200) {
        return null;
      }

      return response.data;
    } catch (e) {
      return null
    }

  },

  /**
   * Returns the response from the api
   * @returns {Array} user_ids
   */
  async fetchUserIds (filters) {
    try {

      let response = await http.post(`users/ids`, filters, this.getDefaultConfig());

      if (response.status !== 200) {
        return [];
      }

      return response.data;
    } catch (e) {
      return [];
    }

  },

  /**
   * Returns the response from the api
   * @param {int} id
   * @returns {boolean} true if successful, false otherwise
   */
  async sendForgetUserEmail (id) {
    try {
      let response = await http.post(`/users/${id}/email/forget-user`, null, this.getDefaultConfig());
      return response.status === 200;
    } catch (e) {
      return false;
    }
  },

  /**
   * Returns the response from the api
   * @param {int} id
   * @returns {boolean} true if successful, false otherwise
   */
  async forgetUser (id) {
    try {
      let response = await http.delete(`/users/${id}`, this.getDefaultConfig());
      return response.status === 200;
    } catch (e) {
      return false;
    }
  },

  /**
   *
   * @param {int} user_id
   * @param {Object} data
   * @param {String} data.message
   * @param {String} data.eventType
   * @returns {Promise<boolean>}
   */
  async addUserNote(user_id, data) {
    try {
      const response = await http.post(`/users/${user_id}/add-note`, data,this.getDefaultConfig());

      return response.status === 200;
    } catch (e) {
      return false;
    }
  },

  /**
   * Returns the response from the api
   * @param {int} id
   * @returns {Object|null} client object. null otherwise
   */
  async updateUser (id, data) {
    try {
      let response = await http.patch(`users/${id}`, data, this.getDefaultConfig());

      if (response.status !== 200) {
        return null;
      }

      return response.data;
    } catch (e) {
      return null
    }

  },

  /**
   * Returns the response from the api
   * @param {Object} options
   * @returns {Object|null} object containing products if login was successful. null otherwise
   */
  async fetchClients (options = {}) {
    try {
      let response = await http.post(`clients`, options, this.getDefaultConfig());

      if (response.status !== 200) {
        return [];
      }

      response.data.clients = response.data.clients.map((it)=>{
        return new types.Client(it);
      });

      return response.data;
    } catch (e) {
      return []
    }
  },

  /**
   * Returns the response from the api
   * @param {int} id
   * @returns {Object|null} client object. null otherwise
   */
  async fetchClient (id) {
    try {

      let response = await http.get(`clients/${id}`, this.getDefaultConfig());

      if (response.status !== 200) {
        return null;
      }

      return response.data;
    } catch (e) {
      return null
    }

  },

  /**
   * Returns the response from the api
   * @param {int} id
   * @returns {Object|null} client object. null otherwise
   */
  async updateClient (id, data) {
    try {
      let response = await http.patch(`clients/${id}`, data, this.getDefaultConfig());

      if (response.status !== 200) {
        return null;
      }

      return response.data;
    } catch (e) {
      return null
    }

  },

  /**
   * Returns the response from the api
   * @param {int} id
   * @returns {boolean} true if successful, false otherwise
   */
  async sendForgetClientEmail (id) {
    try {
      let response = await http.post(`/clients/${id}/email/forget-client`, null, this.getDefaultConfig());
      return response.status === 200;
    } catch (e) {
      return false;
    }
  },

  /**
   * Returns the response from the api
   * @param {int} id
   * @returns {boolean} true if successful, false otherwise
   */
  async forgetClient (id) {
    try {
      let response = await http.delete(`/clients/${id}`, this.getDefaultConfig());
      return response.status === 200;
    } catch (e) {
      return false;
    }
  },

  /**
   * Returns the response from the api
   * @param {Object} options
   * @param {bool} legacy
   * @param raw
   * @returns {Object|null} object containing referral check invoices if login was successful. null otherwise
   */
  async fetchReferralChecks (options = {}, legacy, raw = false) {
    try {
      let url = legacy ? `invoices/transactions` : `referrals/checks`;
      let response = await http.post(url, options, this.getDefaultConfig());

      if (response.status !== 200) {
        return [];
      }

      if (raw) {
        return legacy ? response.data.paymentTransactions : response.data.referralChecks;
      }

      if (legacy) {
        response.data.checks = response.data.paymentTransactions.map((it) => {
          return new types.PaymentTransaction(it);
        });
      }
      else {
        response.data.checks = response.data.referralChecks.map((it) => {
          return new types.ReferralCheck(it);
        });
      }

      return response.data;
    } catch (e) {
      return []
    }

  },

  /**
   * Returns the response from the api
   * @param {int} paymentTransactionId
   * @param data
   * @returns {Object|null} payment transaction object. null otherwise
   */
  async patchPaymentTransaction(paymentTransactionId, data) {
    try {
      let response = await http.patch(`invoices/transactions/${paymentTransactionId}`, data, this.getDefaultConfig());

      if (response.status !== 200) {
        return null;
      }

      return response.data;
    }
    catch (e) {
      return null;
    }

  },

  /**
   * @param id
   * @param data
   * @returns {Object|null} payment transaction object. null otherwise
   */
  async patchReferralCheck(id, data) {
    try {
      let response = await http.patch(`referrals/checks/${id}`, data, this.getDefaultConfig());

      if (response.status !== 200) {
        return null;
      }

      return response.data;
    }
    catch (e) {
      return null;
    }

  },

  /**
   * @param { number } cyclopediaId
   * @return {Promise<[]|null>}
   */
  async getDykProducts(cyclopediaId) {
    try {
      let response = await http.get(`cyclopedias/${cyclopediaId}/dyk-products`, this.getDefaultConfig());

      if (response.status !== 200) {
        return [];
      }

      return response.data;
    }
    catch (e) {
      return [];
    }
  },

  async getAvailableDykProductsToAdd(cyclopediaId) {
    try {
      let response = await http.get(`cyclopedias/${cyclopediaId}/dyk-products/available-to-add`, this.getDefaultConfig());

      if (response.status !== 200) {
        return [];
      }

      return response.data;
    }
    catch (e) {
      return [];
    }
  },

  /**
   *
   * @param cyclopedia_id
   * @param dyk_products
   * @returns {Promise<boolean>}
   */
  async sortDykProducts(cyclopedia_id, dyk_products) {
    try {
      let response = await http.patch(`cyclopedias/${cyclopedia_id}/dyk-products/sort`, dyk_products, this.getDefaultConfig());
      return response.status === 200;
    }
    catch (e) {
      return false;
    }
  },

  /**
   *
   * @param cyclopedia_id
   * @param product_id
   * @returns {Promise<boolean>}
   */
  async addDykProducts(cyclopedia_id, product_id) {
    try {
      let response = await http.post(`cyclopedias/${cyclopedia_id}/dyk-products/${product_id}`, null, this.getDefaultConfig());
      return response.status === 200;
    }
    catch (e) {
      return false;
    }
  },

  /**
   * @param { Number } cyclopedia_id
   * @param { Number } product_id
   * @return {Promise<boolean>}
   */
  async deleteDykProduct(cyclopedia_id, product_id) {
    try {
      let response = await http.delete(`cyclopedias/${cyclopedia_id}/dyk-products/${product_id}`, this.getDefaultConfig());
      return response.status === 200;
    }
    catch (e) {
      return false;
    }
  },

  /**
   * Get all trusted partners
   * @return {Promise<T|*[]>}
   */
  async getTrustedPartners() {
    try {
      let response = await http.get('trusted-partners/all', this.getDefaultConfig());
      return response.status === 200 ? response.data : [];
    }
    catch (e) {
      console.log('Error getTrustedPartners: ', e);
      return [];
    }
  },

  /**
   * Get Trusted Partner by id
   * @param id
   * @return {Promise<null|T>}
   */
  async getTrustedPartner(id) {
    try {
      let response = await http.get(`trusted-partners/${id}`, this.getDefaultConfig());
      return response.status === 200 ? response.data : null;
    } catch (e) {
      console.log('getTrustedPartner: ', e);
      return null;
    }
  },

  /**
   * Create Trusted Partner
   * @param trusted_partner
   * @return {Promise<boolean>}
   */
  async createTrustedPartner(trusted_partner) {
    try {
      let response = await http.post('trusted-partners', trusted_partner, this.getDefaultConfig());
      return response.status === 200;
    }
    catch (e) {
      console.log('Error getTrustedPartners: ', e);
      return false;
    }
  },

  /**
   * Update existing trusted partner
   * @param trusted_partner
   * @return {Promise<boolean>}
   */
  async updateTrustedPartner(trusted_partner) {
    try {
      let response = await http.patch('trusted-partners', trusted_partner, this.getDefaultConfig());
      return response.status === 200;
    }
    catch (e) {
      console.log('Error updateTrustedPartner: ', e);
      return false;
    }
  },

  /**
   * Update list of localizations for a trusted partner
   * @param localizations
   * @return {Promise<boolean>}
   */
  async updateTrustedPartnerLocalizations(localizations) {
    try {
      let requests = [];
      let success = true;

      for (const localization of localizations) {
        requests.push(await http.post('trusted-partners/localization', localization, this.getDefaultConfig()));
      }

      const results = await Promise.all(requests);

      results.forEach((result) => {
        if(result.status !== 200) success = false;
      });
      return success;
    }
    catch (e) {
      console.log('Error updateTrustedPartnerLocalizations: ', e);
      return false;
    }
  },

  /**
   * Update Trusted Partner Image
   * @param data
   * @return {Promise<boolean>}
   */
  async updateTrustedPartnerImage(data) {
    try {
      let config = this.getDefaultConfig();
      config.headers['Content-Type'] = 'multipart/form-data';

      let formData = new FormData();
      formData.append('image', data.image);
      formData.append('trusted_partner_id', data.trusted_partner_id);

      let response = await http.post('trusted-partners/image', formData, config);
      return response.status === 201;
    }
    catch (e) {
      console.log('Error updateTrustedPartnerImage: ', e);
      return false;
    }
  },

  /**
   * Add Company Relation to Trusted Partner
   * @param {Number} trusted_partner_id
   * @param {Number} company_id
   * @returns {Promise<boolean>}
   */
  async addTrustedPartnerCompany(trusted_partner_id, company_id) {
    try {
      const response = await http.post(`trusted-partners/${trusted_partner_id}/company/${company_id}`, {}, this.getDefaultConfig());
      return response.status === 200;
    }
    catch (e) {
      console.log('Error updateTrustedPartner: ', e);
      return false;
    }
  },

  /**
   * Delete Company Relation from Trusted Partner
   * @param {Number} trusted_partner_id
   * @param {Number} company_id
   * @returns {Promise<boolean>}
   */
  async deleteTrustedPartnerCompany(trusted_partner_id, company_id) {
    try {
      const response = await http.delete(`trusted-partners/${trusted_partner_id}/company/${company_id}`,  this.getDefaultConfig());
      return response.status === 200;
    }
    catch (e) {
      console.log('Error updateTrustedPartner: ', e);
      return false;
    }
  },

  /**
   * Get all user referrals
   * @return {Promise<T|*[]>}
   */
  async getUserReferrals(userId) {
    try {
      let response = await http.get(`users/${userId}/referrals`, this.getDefaultConfig());
      return response.status === 200 ? response.data : [];
    }
    catch (e) {
      console.log('Error getUserReferrals: ', e);
      return [];
    }
  },

  async getUserReferralsBreakdown(userId) {
    try {
      let response = await http.get(`users/${userId}/referrals/breakdown`, this.getDefaultConfig());
      return response.status === 200 ? response.data : [];
    }
    catch (e) {
      console.log('Error getUserReferrals: ', e);
      return [];
    }
  },

  /**
   * Get orders
   * @param queryParams
   * @return {Promise<T|*[]>}
   */
  async getOrders(queryParams) {
    const { page, limit, filters, sortBy, orderDirection, search } = queryParams;
    let config = this.getDefaultConfig();
    config.params = {page: page, search: search, limit: limit, sortBy: sortBy, orderDirection: orderDirection, filters: JSON.stringify(filters)}

    try {
      let response = await http.get(`orders`, config);
      return response.status === 200 ? response.data : [];
    }
    catch (e) {
      console.log('Error getOrders: ', e);
      return [];
    }
  },

  /**
   * Groups orders by similar shipping address
   * @return {Promise<T|*[]>}
   */
  async groupOrderByAddress() {
    try {
      let response = await http.get(`orders/group-by-address`, this.getDefaultConfig());
      return response.status === 200 ? response.data : [];
    }
    catch (e) {
      console.log('Error groupByAddress: ', e);
      return [];
    }
  },

  async getOrderById(orderId) {
    try {
      let response = await http.get(`orders/${orderId}`, this.getDefaultConfig());
      return response.status === 200 ? response.data : null;
    }
    catch (e) {
      console.log('Error groupByAddress: ', e);
      return null;
    }
  },

  async updateOrder(orderId, data) {
    try {
      let response = await http.post(`orders/${orderId}`, data,this.getDefaultConfig());
      return response.status === 200;
    }
    catch (e) {
      console.log('Error updateOrder: ', e);
      return false;
    }
  },

  async updateOrderShipTime(orderId) {
    try {
      let response = await http.patch(`orders/${orderId}/update-ship-time`, {}, this.getDefaultConfig());
      return response.status === 200;
    }
    catch (e) {
      console.log('Error updateOrderShipTime: ', e);
      return false;
    }
  },

  async createOrderShipments(orderId, lineItems) {
    try {
      let response = await http.post(`orders/${orderId}/shipments`, lineItems, this.getDefaultConfig());
      return response.status === 200;
    }
    catch (e) {
      console.log('Error createOrderShipments: ', e);
      return false;
    }
  },

  async createOrderShipmentLabel(orderId, shipmentLocationId, shippingLabel) {
    try {
      let response = await http.post(`orders/${orderId}/shipments/${shipmentLocationId}/shipping-label`, shippingLabel, this.getDefaultConfig());
      return response.status === 200;
    } catch (e) {
      console.log('Error createOrderShipmentLabel: ', e);
      return false;
    }
  },

  async getPartnerInvcs(source, queryParams) {
    try {
      const { page, limit } = queryParams
      const config = this.getDefaultConfig()
      config.params = { page, limit }

      let response = await http.get(`orders/partner-invcs/${source}`, config);
      return response.status === 200 ? response.data : [];
    } catch (e) {
      console.error('getPartnerInvcs: ', e);
      return [];
    }
  },

  async getPartnerInvc(source, invcNumber) {
    try {
      let response = await http.get(`orders/partner-invcs/${source}/${invcNumber}`, this.getDefaultConfig());
      return response.status === 200 ? response.data : null;
    } catch (e) {
      console.error('getPartnerInvc: ', e);
      return null;
    }
  },

  async getPartnerUnbilledInvc(source) {
    try {
      let response = await http.get(`orders/partner-unbilled-invc/${source}`, this.getDefaultConfig());
      return response.status === 200 ? response.data : null;
    } catch (e) {
      console.error('getPartnerUnbilledInvc: ', e);
      return null;
    }
  },

  async billPartnerInvcs(source, orderIds, invcNumber) {
    try {
      const body = {orderIds: orderIds, invcNumber: invcNumber};
      await http.post(`orders/partner-unbilled-invc/${source}/bill`, body ,this.getDefaultConfig());
      return [null, true];
    } catch (e) {
      console.error('billPartnerInvcs: ', e);
      const { status, data } = e.response;
      return [(status === 400 ? data.error : 'Something went wrong'), false];
    }
  },

  async getPartnerOrderStatuses(source) {
    try {
      let response = await http.get(`orders/partner-statuses/${source}`, this.getDefaultConfig());
      return response.status === 200 ? response.data : null;
    } catch (e) {
      console.error('getPartnerInvcs: ', e);
      return null;
    }
  },

  async getAllFulfillmentCenters() {
    try {
      let response = await http.get(`fulfillment-centers/all`,this.getDefaultConfig());
      return response.status === 200 ? response.data : [];
    }
    catch (e) {
      console.log('Error updateOrder: ', e);
      return [];
    }
  },

  /**
   * Get All Push Notification Logs
   * @return {Promise<T|*[]>}
   */
  async getAllPushNotificationLogs() {
    try {
      let response = await http.get(`admin-logs/push-notifications`, this.getDefaultConfig());
      return response.status === 200 ? response.data : [];
    }
    catch (e) {
      console.log('Error getAllLogsByType: ', e)
      return [];
    }
  },

  /**
   * Create Push Notification Log
   * @param data
   * @return {Promise<boolean>}
   */
  async createPushNotificationLog(data) {
    try {
      let response = await http.post(`admin-logs/push-notifications`, data, this.getDefaultConfig());
      return response.status === 200;
    }
    catch (e) {
      console.log('Error getAllLogsByType: ', e)
      return false;
    }
  },

  /**
   * Create or Update Company Product Data
   * @param { Number } productId
   * @param data
   * @return {Promise<boolean>}
   */
  async upsertCompanyProductData(productId, data) {
    try {
      let response = await http.post(`/products/${productId}/company-product-data`, data, this.getDefaultConfig());
      return response.status === 200 || response.status === 201;
    }
    catch (e) {
      console.log('Error upsertCompanyProductData: ', e)
      return false;
    }
  },

  /**
   * Delete Company Product Data
   * @param { Number } id
   * @param { Number } productId
   * @return {Promise<boolean>}
   */
  async deleteCompanyProductData(id, productId) {
    try {
      let response = await http.delete(`/products/${productId}/company-product-data/${id}`, this.getDefaultConfig());
      return response.status === 200;
    }
    catch (e) {
      console.log('Error deleteCompanyProductData: ', e)
      return false;
    }
  },

  /**
   * Get all Protocol Categories
   * @return {Promise<Array>}
   */
  async getProtocolCategories() {
    try {
      let response = await http.get('protocol-categories/all', this.getDefaultConfig());
      return response.status === 200 ? response.data : [];
    }
    catch (e) {
      console.log('Error getProtocolCategories: ', e);
      return [];
    }
  },

  /**
   * Get a Protocol Category by id (returns nested translations)
   * @param {Number} id
   * @return {Promise<Object|null>}
   */
  async getProtocolCategory(id) {
    try {
      let response = await http.get(`protocol-categories/${id}`, this.getDefaultConfig());
      return response.status === 200 ? response.data : null;
    }
    catch (e) {
      console.log('Error getProtocolCategory: ', e);
      return null;
    }
  },

  /**
   * Create a Protocol Category
   * @param {Object} protocolCategory - { name_internal }
   * @return {Promise<Object|null>} created protocol category, or null on failure
   */
  async createProtocolCategory(protocolCategory) {
    try {
      let response = await http.post('protocol-categories/create', protocolCategory, this.getDefaultConfig());
      return (response.status === 200 || response.status === 201) ? response.data : null;
    }
    catch (e) {
      console.log('Error createProtocolCategory: ', e);
      return null;
    }
  },

  /**
   * Update a Protocol Category
   * @param {Number} id
   * @param {Object} data - { name_internal }
   * @return {Promise<boolean>}
   */
  async updateProtocolCategory(id, data) {
    try {
      let response = await http.patch(`protocol-categories/${id}`, data, this.getDefaultConfig());
      return response.status === 200;
    }
    catch (e) {
      console.log('Error updateProtocolCategory: ', e);
      return false;
    }
  },

  /**
   * Delete a Protocol Category
   * @param {Number} id
   * @return {Promise<boolean>}
   */
  async deleteProtocolCategory(id) {
    try {
      let response = await http.delete(`protocol-categories/${id}`, this.getDefaultConfig());
      return response.status === 200;
    }
    catch (e) {
      console.log('Error deleteProtocolCategory: ', e);
      return false;
    }
  },

  /**
   * Get all translations for a Protocol Category
   * @param {Number} categoryId
   * @return {Promise<Array>}
   */
  async getProtocolCategoryTranslations(categoryId) {
    try {
      let response = await http.get(`protocol-categories/${categoryId}/translations/all`, this.getDefaultConfig());
      return response.status === 200 ? response.data : [];
    }
    catch (e) {
      console.log('Error getProtocolCategoryTranslations: ', e);
      return [];
    }
  },

  /**
   * Create a translation for a Protocol Category
   * @param {Number} categoryId
   * @param {Object} translation - { language_id, name }
   * @return {Promise<Object|null>}
   */
  async createProtocolCategoryTranslation(categoryId, translation) {
    try {
      let response = await http.post(`protocol-categories/${categoryId}/translations/create`, translation, this.getDefaultConfig());
      return (response.status === 200 || response.status === 201) ? response.data : null;
    }
    catch (e) {
      console.log('Error createProtocolCategoryTranslation: ', e);
      return null;
    }
  },

  /**
   * Update a Protocol Category translation
   * @param {Number} categoryId
   * @param {Number} languageId
   * @param {Object} data - { name }
   * @return {Promise<boolean>}
   */
  async updateProtocolCategoryTranslation(categoryId, languageId, data) {
    try {
      let response = await http.patch(`protocol-categories/${categoryId}/translations/${languageId}`, data, this.getDefaultConfig());
      return response.status === 200;
    }
    catch (e) {
      console.log('Error updateProtocolCategoryTranslation: ', e);
      return false;
    }
  },

  /**
   * Delete a Protocol Category translation
   * @param {Number} categoryId
   * @param {Number} languageId
   * @return {Promise<boolean>}
   */
  async deleteProtocolCategoryTranslation(categoryId, languageId) {
    try {
      let response = await http.delete(`protocol-categories/${categoryId}/translations/${languageId}`, this.getDefaultConfig());
      return response.status === 200;
    }
    catch (e) {
      console.log('Error deleteProtocolCategoryTranslation: ', e);
      return false;
    }
  },

  // ── Protocol Usage Types ──────────────────────────────────────────

  async getProtocolUsageTypes() {
    try {
      let response = await http.get('protocol-usage-types/all', this.getDefaultConfig());
      return response.status === 200 ? response.data : [];
    }
    catch (e) {
      console.log('Error getProtocolUsageTypes: ', e);
      return [];
    }
  },

  async createProtocolUsageType(data) {
    try {
      let response = await http.post('protocol-usage-types/create', data, this.getDefaultConfig());
      return (response.status === 200 || response.status === 201) ? response.data : null;
    }
    catch (e) {
      console.log('Error createProtocolUsageType: ', e);
      return null;
    }
  },

  async updateProtocolUsageType(id, data) {
    try {
      let response = await http.patch(`protocol-usage-types/${id}`, data, this.getDefaultConfig());
      return response.status === 200;
    }
    catch (e) {
      console.log('Error updateProtocolUsageType: ', e);
      return false;
    }
  },

  async deleteProtocolUsageType(id) {
    try {
      let response = await http.delete(`protocol-usage-types/${id}`, this.getDefaultConfig());
      return response.status === 200;
    }
    catch (e) {
      console.log('Error deleteProtocolUsageType: ', e);
      return false;
    }
  },

  // ── Protocols ─────────────────────────────────────────────────────

  async getProtocols() {
    try {
      let response = await http.get('protocols/all', this.getDefaultConfig());
      return response.status === 200 ? response.data : [];
    }
    catch (e) {
      console.log('Error getProtocols: ', e);
      return [];
    }
  },

  async getProtocol(id) {
    try {
      let response = await http.get(`protocols/${id}`, this.getDefaultConfig());
      return response.status === 200 ? response.data : null;
    }
    catch (e) {
      console.log('Error getProtocol: ', e);
      return null;
    }
  },

  async createProtocol(data) {
    try {
      let response = await http.post('protocols/create', data, this.getDefaultConfig());
      return (response.status === 200 || response.status === 201) ? response.data : null;
    }
    catch (e) {
      console.log('Error createProtocol: ', e);
      return null;
    }
  },

  async updateProtocol(id, data) {
    try {
      let response = await http.patch(`protocols/${id}`, data, this.getDefaultConfig());
      return response.status === 200;
    }
    catch (e) {
      console.log('Error updateProtocol: ', e);
      return false;
    }
  },

  async deleteProtocol(id) {
    try {
      let response = await http.delete(`protocols/${id}`, this.getDefaultConfig());
      return response.status === 200;
    }
    catch (e) {
      console.log('Error deleteProtocol: ', e);
      return false;
    }
  },

  // ── Protocol Translations ─────────────────────────────────────────

  async createProtocolTranslation(protocolId, data) {
    try {
      let response = await http.post(`protocols/${protocolId}/translations/create`, data, this.getDefaultConfig());
      return (response.status === 200 || response.status === 201) ? response.data : null;
    }
    catch (e) {
      console.log('Error createProtocolTranslation: ', e);
      return null;
    }
  },

  async updateProtocolTranslation(protocolId, languageId, data) {
    try {
      let response = await http.patch(`protocols/${protocolId}/translations/${languageId}`, data, this.getDefaultConfig());
      return response.status === 200;
    }
    catch (e) {
      console.log('Error updateProtocolTranslation: ', e);
      return false;
    }
  },

  async deleteProtocolTranslation(protocolId, languageId) {
    try {
      let response = await http.delete(`protocols/${protocolId}/translations/${languageId}`, this.getDefaultConfig());
      return response.status === 200;
    }
    catch (e) {
      console.log('Error deleteProtocolTranslation: ', e);
      return false;
    }
  },

  // ── Protocol Content ──────────────────────────────────────────────

  async createProtocolContent(protocolId, data) {
    try {
      let response = await http.post(`protocols/${protocolId}/contents/create`, data, this.getDefaultConfig());
      return (response.status === 200 || response.status === 201) ? response.data : null;
    }
    catch (e) {
      console.log('Error createProtocolContent: ', e);
      return null;
    }
  },

  async updateProtocolContent(protocolId, contentId, data) {
    try {
      let response = await http.patch(`protocols/${protocolId}/contents/${contentId}`, data, this.getDefaultConfig());
      return response.status === 200;
    }
    catch (e) {
      console.log('Error updateProtocolContent: ', e);
      return false;
    }
  },

  async deleteProtocolContent(protocolId, contentId) {
    try {
      let response = await http.delete(`protocols/${protocolId}/contents/${contentId}`, this.getDefaultConfig());
      return response.status === 200;
    }
    catch (e) {
      console.log('Error deleteProtocolContent: ', e);
      return false;
    }
  },

  // ── Protocol Products ─────────────────────────────────────────────

  async getProtocolProducts(protocolId) {
    try {
      let response = await http.get(`protocols/${protocolId}/products/all`, this.getDefaultConfig());
      return response.status === 200 ? response.data : [];
    }
    catch (e) {
      console.log('Error getProtocolProducts: ', e);
      return [];
    }
  },

  async addProtocolProduct(protocolId, productId) {
    try {
      let response = await http.post(`protocols/${protocolId}/products/create`, { product_id: productId }, this.getDefaultConfig());
      return (response.status === 200 || response.status === 201);
    }
    catch (e) {
      console.log('Error addProtocolProduct: ', e);
      return false;
    }
  },

  async removeProtocolProduct(protocolId, productId) {
    try {
      let response = await http.delete(`protocols/${protocolId}/products/${productId}`, this.getDefaultConfig());
      return response.status === 200;
    }
    catch (e) {
      console.log('Error removeProtocolProduct: ', e);
      return false;
    }
  },

  // ── Product Protocol Usage Types ──────────────────────────────────

  async getProductProtocolUsageTypes(productId) {
    try {
      let response = await http.get(`products/${productId}/protocol-usage-types/all`, this.getDefaultConfig());
      return response.status === 200 ? response.data : [];
    }
    catch (e) {
      console.log('Error getProductProtocolUsageTypes: ', e);
      return [];
    }
  },

  async addProductProtocolUsageType(productId, usageTypeId) {
    try {
      let response = await http.post(`products/${productId}/protocol-usage-types/create`, { usage_type_id: usageTypeId }, this.getDefaultConfig());
      return (response.status === 200 || response.status === 201);
    }
    catch (e) {
      console.log('Error addProductProtocolUsageType: ', e);
      return false;
    }
  },

  async removeProductProtocolUsageType(productId, usageTypeId) {
    try {
      let response = await http.delete(`products/${productId}/protocol-usage-types/${usageTypeId}`, this.getDefaultConfig());
      return response.status === 200;
    }
    catch (e) {
      console.log('Error removeProductProtocolUsageType: ', e);
      return false;
    }
  }
}
